import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";
import { useNavigate } from "react-router-dom";
import {
  useLazyGetManageReviewsQuery,
  useLazyDeleteReviewQuery,
  useUpdateReviewMutation,
} from "../../service/manageReviews";
import ConfirmationDialog from "../../Modals/confirmationModal";
import { PRODUCT_STATUS } from "../../constants/enums";
import Pagination from "../../components/Panigation";

interface Review {
  _id: number;
  name: string;
  email: string;
  rating: number;
  message: string;
  createdAt: string;
  status: number;
  product?: {
    _id: string; // Add this line
    name: string;
  };
  addedByDetails?: {
    _id: string; // Add this line
    fullName: string;
    email?: string;
  };
}

interface PromotionFeedbackTableProps {
  showPromotionColumn?: boolean;
  showUserColumn?: boolean;
}

export default function PromotionFeedbackTable({
  showPromotionColumn = true,
  showUserColumn = true,
}: PromotionFeedbackTableProps) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [rows, setRows] = useState<Review[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Review | null>(null);
  const [getManageReviews] = useLazyGetManageReviewsQuery();
  const [deleteReview] = useLazyDeleteReviewQuery();
  const [updateReview] = useUpdateReviewMutation();

  const getReviews = async () => {
    try {
      let status = tab;
      if (tab === 0) {
        status = 1;
      } else if (tab === 1) {
        status = 2;
      } else if (tab === 2) {
        status = 3;
      }

      const response = await getManageReviews({
        page,
        limit,
        status,
        search: debouncedSearchTerm,
      });

      setRows(response?.data?.data?.list || []);
      setTotalCount(response?.data?.data?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const handleDeleteReview = async () => {
    if (!selectedUser) {
      console.error("No user selected for deletion");
      return;
    }

    try {
      const response = await deleteReview({ id: selectedUser._id });
      console.log("Review deleted:", response);
      // Refresh the reviews list after deletion
      await getReviews();
      // Close the delete modal
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting review:", error);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleTab = (_: React.SyntheticEvent, v: number) => setTab(v);

  const filteredProducts = (status: number) =>
    rows.filter((p) => p.status === status);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    getReviews();
  };

  const totalPages = Math.ceil(totalCount / limit);

  useEffect(() => {
    getReviews();
  }, [page, limit, tab, debouncedSearchTerm]);

  const handleApprove = async (id: string | number) => {
    try {
      await updateReview({
        id: id,
        body: { status: PRODUCT_STATUS.APPROVED },
      });
      await getReviews();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error approving review:", error);
    }
  };

  const handleReject = async (id: string | number) => {
    try {
      await updateReview({
        id: id,
        body: { status: PRODUCT_STATUS.REJECTED },
      });
      await getReviews();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error rejecting review:", error);
    }
  };

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            placeholder="Search..."
          />
        </div>
      </div>

      <Box className="custom_tabs1" sx={{ width: "100%", mt: 2 }}>
        <Tabs value={tab} onChange={handleTab} className="custom_tabs_links">
          <Tab label={`Pending `} />
          <Tab label={`Approved `} />
          <Tab label={`Rejected `} />
        </Tabs>
      </Box>

      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow hover>
              <TableCell>User Detail</TableCell>
              <TableCell>Product Detail</TableCell>
              <TableCell>Feedback Provided</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date & Time</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: Review) => (
              <TableRow hover key={row._id}>
                {showUserColumn && (
                  <TableCell>
                    <Box
                      component="a"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          `/manage-users/detail/${row?.addedByDetails?._id}`
                        );
                      }}
                      className="user_block click_action"
                    >
                      <p>
                        <b>{row?.addedByDetails?.fullName}</b>
                        {/* <span>{row.addedByDetails.email}</span> */}
                        {/* <span>{row.phone}</span> */}
                      </p>
                    </Box>
                  </TableCell>
                )}
                {showUserColumn && (
                  <TableCell>
                    <Box
                      component="a"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          `/manage-products/detail/${row?.product?._id}`
                        );
                      }}
                      className="user_block click_action"
                    >
                      <p>
                        <b>{row?.product?.name}</b>
                        {/* <span>{row.email}</span> */}
                        {/* <span>{row.phone}</span> */}
                      </p>
                    </Box>
                  </TableCell>
                )}
                <TableCell>{row?.message}</TableCell>
                <TableCell>
                  <p>
                    <b>
                      {row?.status === PRODUCT_STATUS.APPROVED
                        ? "Approved"
                        : row?.status === PRODUCT_STATUS.REJECTED
                        ? "Rejected"
                        : "Pending"}
                    </b>
                  </p>
                </TableCell>
                <TableCell>
                  {new Date(row.createdAt).toLocaleDateString("en-GB")}
                </TableCell>
                <TableCell>
                  <div className="btn_flex">
                    {row?.status === PRODUCT_STATUS.PENDING && (
                      <>
                        <Button
                          onClick={() => handleApprove(row._id)}
                          className="btn btn_secondary"
                          size="small"
                        >
                          Approve
                        </Button>

                        <Button
                          onClick={() => handleReject(row._id)}
                          className="btn btn_danger"
                          size="small"
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    <div className="table_actions v2">
                      <IconButton
                        onClick={() =>
                          navigate(`/manage-reviews/view/${row._id}`)
                        }
                      >
                        <img src="/static/images/eye_icon.svg" alt="View" />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setSelectedUser(row);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <img
                          src="/static/images/trash2_icon.svg"
                          alt="Delete"
                        />
                      </IconButton>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No reviews found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > 0 && (
        <Pagination
          module={rows}
          page={page}
          onPageChange={handlePageChange}
          totalPages={totalPages}
          setPage={setPage}
          totalCount={totalCount}
        />
      )}

      <ConfirmationDialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteReview}
        title="Delete Review"
        confirmText="Delete"
        cancelText="Cancel"
        message={`Are you sure you want to delete review  `}
      />
    </>
  );
}
